import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showError, showSuccess } from '../../../../core/alert/sweetAlertConfig';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import { AxiosResponse } from 'axios';

interface CreateDoorModuleProps {
  show: boolean;
  onHide: () => void;
  roomId: number;
  roomName: string;
  onSuccess: () => void;
}

const CreateDoorModule: React.FC<CreateDoorModuleProps> = ({
  show,
  onHide,
  roomId,
  roomName,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkPermission('ROLE_CREATE_DOOR')) {
      showError('You do not have permission to create doors.');
      return;
    }
    if (!formData.name.trim()) {
      showError('Door name is required');
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
        roomId,
      };
      const response: AxiosResponse = await apiDefinitions.addDoor(payload);
      if (response.status === 200) {
        const data = response.data;
        if (data.status === 'S1000' || data.status === 'SUCCESS') {
          showSuccess("Door created successfully");
          onSuccess();
          setFormData({ name: '', description: '' });
          onHide();
        } else {
          showError(data.statusDescription || 'Failed to create door');
        }
      } else {
        showError(response.data?.statusDescription || 'Failed to create door');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
      } else if (err.response?.status === 400) {
        showError('Invalid door data. Please check the input.');
      } else if (err.response?.status === 404) {
        showError('Room not found.');
      } else if (err.response?.status === 409) {
        showError('Door name already exists in this room.');
      } else if (err.response?.status === 500) {
        showError('Failed to create door due to server error. Please contact support.');
      } else {
        showError('Failed to create door. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Door to {roomName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label">Door Name</label>
            <input
              type="text"
              className="form-control"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              disabled={isLoading}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              disabled={isLoading}
            />
          </div>
          <div className="text-end">
            <button
              type="button"
              className="btn btn-primary me-2"
              onClick={onHide}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-success"
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create Door'}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default CreateDoorModule;